import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { ShieldCheck, KeyRound, Loader2, Check, AlertCircle, Trash2, LogOut, Sparkles } from 'lucide-react';

/**
 * Doctor-facing PIN + trusted-devices panel — drops into the Profile tab.
 *
 * Goals:
 *  - Nudge doctors to set a PIN ASAP (it saves *them* time on every login).
 *  - Let them change PIN safely (current PIN required).
 *  - Show every device that's currently allowed PIN-only login + revoke any.
 *  - "Sign out everywhere" for the stolen-laptop scare.
 */
export default function PinSettings() {
  const [pinSet, setPinSet] = useState(null);
  const [pinSetAt, setPinSetAt] = useState(null);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, d] = await Promise.all([
        api.get('/auth/pin/status'),
        api.get('/auth/devices'),
      ]);
      setPinSet(!!s.data.pin_set);
      setPinSetAt(s.data.pin_set_at || null);
      setDevices(d.data.devices || []);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setMsg(''); setErr('');
    if (!/^\d{6}$/.test(newPin)) { setErr('PIN must be 6 digits.'); return; }
    if (newPin !== confirmPin) { setErr('PINs do not match.'); return; }
    setBusy(true);
    try {
      const body = { pin: newPin };
      if (pinSet) body.current_pin = currentPin;
      const { data } = await api.post('/auth/pin/set', body);
      setMsg(data.message || 'PIN saved.');
      setShowForm(false);
      setCurrentPin(''); setNewPin(''); setConfirmPin('');
      loadAll();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const revokeOne = async (id) => {
    if (!window.confirm('Sign this device out of DocSite? Next login from there will need an SMS code again.')) return;
    try { await api.delete(`/auth/devices/${id}`); loadAll(); } catch (e) { /* ignore */ }
  };

  const revokeAll = async () => {
    if (!window.confirm('Sign out of DocSite on every device? You will need an SMS code on the next login from each one.')) return;
    try { await api.post('/auth/devices/revoke-all'); loadAll(); } catch (e) { /* ignore */ }
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6 space-y-5" data-testid="pin-settings">
      <header className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center shrink-0">
          <KeyRound size={20} className="text-violet-700" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Fast sign-in with a 6-digit PIN
          </h3>
          <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
            Set a PIN once, then sign in instantly on every device you use — no waiting for SMS, no notification taps. SMS keeps protecting you on brand-new devices.
          </p>
        </div>
      </header>

      {!pinSet && !loading && (
        <div className="rounded-xl border border-violet-200 bg-violet-50 p-4 flex items-start gap-3" data-testid="pin-set-cta">
          <Sparkles size={16} className="text-violet-600 shrink-0 mt-0.5" />
          <div className="flex-1 text-xs text-violet-900">
            <p className="font-semibold">You haven't set a PIN yet.</p>
            <p className="mt-0.5">Set one in 30 seconds — every future login on this device becomes a single tap.</p>
          </div>
          <button onClick={() => setShowForm(true)}
            className="text-xs font-semibold px-3 py-1.5 rounded-full bg-violet-600 text-white hover:bg-violet-700"
            data-testid="pin-set-button">
            Set PIN
          </button>
        </div>
      )}

      {pinSet && !showForm && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3">
          <Check size={16} className="text-emerald-600 shrink-0" />
          <div className="flex-1 text-xs text-emerald-900">
            <p className="font-semibold">PIN active</p>
            {pinSetAt && <p className="mt-0.5 text-emerald-700">Set on {new Date(pinSetAt).toLocaleDateString()}</p>}
          </div>
          <button onClick={() => setShowForm(true)} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-100"
            data-testid="pin-change-button">
            Change PIN
          </button>
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className="space-y-3 border border-slate-200 rounded-xl p-4 bg-slate-50">
          {err && <div className="text-xs text-red-600 bg-red-50 p-2 rounded flex items-center gap-1.5" data-testid="pin-form-error"><AlertCircle size={12} />{err}</div>}
          {pinSet && (
            <PinInput label="Current PIN" value={currentPin} setValue={setCurrentPin} testid="pin-current-input" autoFocus />
          )}
          <PinInput label="New PIN (6 digits)" value={newPin} setValue={setNewPin} testid="pin-new-input" autoFocus={!pinSet} />
          <PinInput label="Confirm new PIN" value={confirmPin} setValue={setConfirmPin} testid="pin-confirm-input" />
          <p className="text-[11px] text-slate-500">Avoid 1234, repeated digits, or your birthdate.</p>
          <div className="flex gap-2">
            <button type="submit" disabled={busy}
              className="flex-1 rounded-full py-2.5 bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700 disabled:opacity-50 flex items-center justify-center gap-1.5"
              data-testid="pin-form-submit">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} />}
              {busy ? 'Saving…' : 'Save PIN'}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setErr(''); setCurrentPin(''); setNewPin(''); setConfirmPin(''); }}
              className="rounded-full px-4 py-2.5 bg-white border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-50"
              data-testid="pin-form-cancel">
              Cancel
            </button>
          </div>
        </form>
      )}

      {msg && <div className="text-xs text-emerald-700 bg-emerald-50 p-2 rounded flex items-center gap-1.5"><Check size={12} />{msg}</div>}

      {/* Trusted devices */}
      {pinSet && (
        <div className="space-y-3 pt-3 border-t border-slate-100">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold text-slate-700 uppercase tracking-widest">Trusted devices</h4>
            {devices.length > 0 && (
              <button onClick={revokeAll} className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800 font-semibold"
                data-testid="pin-signout-all">
                <LogOut size={12} /> Sign out everywhere
              </button>
            )}
          </div>
          {devices.length === 0 ? (
            <p className="text-xs text-slate-500">No trusted devices yet — one will be added the next time you sign in with SMS.</p>
          ) : (
            <ul className="space-y-2">
              {devices.map((d) => (
                <li key={d.id} className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-3" data-testid="pin-device-row">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-800">
                      {summariseUA(d.user_agent)}
                      {d.is_current && <span className="ml-2 text-[10px] font-bold text-violet-700 bg-violet-100 rounded-full px-2 py-0.5">This device</span>}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      {d.geo_city || 'Unknown city'}{d.last_seen_at ? ` · last used ${new Date(d.last_seen_at).toLocaleDateString()}` : ''}
                    </p>
                  </div>
                  <button onClick={() => revokeOne(d.id)} className="text-slate-400 hover:text-red-600" title="Sign out this device" data-testid="pin-device-revoke">
                    <Trash2 size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

function PinInput({ label, value, setValue, testid, autoFocus }) {
  return (
    <div>
      <label className="text-[11px] font-medium text-slate-600 mb-1 block">{label}</label>
      <input
        type="password" inputMode="numeric" maxLength={6} autoFocus={autoFocus}
        className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm text-center font-mono tracking-[0.4em] outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500"
        value={value} onChange={(e) => setValue(e.target.value.replace(/\D/g, '').slice(0, 6))}
        placeholder="••••••" data-testid={testid}
      />
    </div>
  );
}

function summariseUA(ua) {
  if (!ua) return 'Unknown device';
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (/iPad/i.test(ua)) return 'iPad';
  if (/Android/i.test(ua)) return 'Android phone';
  if (/Macintosh/i.test(ua)) return 'Mac (Safari/Chrome)';
  if (/Windows/i.test(ua)) return 'Windows PC';
  return ua.split(' ')[0] || 'Browser';
}
